import type { ReactElement } from 'react';
import { useParams } from 'react-router';

import CalendarSchedulingSection from 'features/master/ui/CalendarSchedulingSection';
import UpdateStockSection from 'features/master/ui/UpdateStockSection';
import { CollapsibleSectionsProvider, useCollapsibleSectionsControls } from 'shared/hooks/collapsibleSections';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { useGetProjectsQuery } from '../api/projects';
import { useGetProjectIntegrationsQuery } from '../services/projects.api';
import CrmConnectionsSection from '../ui/CrmConnectionsSection';
import EmailProcessingSection from '../ui/EmailProcessingSection';
import ExternalLinkConfigSection from '../ui/ExternalLinkConfigSection';
import LookerSection from '../ui/LookerSection';
import MondaySection from '../ui/MondaySection';
import MondaySupportSection from '../ui/MondaySupportSection';
import WhatsAppCallingSection from '../ui/WhatsAppCallingSection';

// Rendered inside the provider so it can reach the controls the sections are wired to.
const SectionCollapseControls = (): ReactElement | null => {
  const controls = useCollapsibleSectionsControls();

  if (!controls) return null;

  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="sm" onClick={controls.expandAll}>
        Expandir todo
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={controls.collapseAll}>
        Colapsar todo
      </Button>
    </div>
  );
};

const IntegrationsView = (): ReactElement => {
  const { masterProjectId } = useParams();
  const id = Number(masterProjectId);

  const { data, isLoading, isError } = useGetProjectIntegrationsQuery(id, { skip: !id });
  const { data: projects } = useGetProjectsQuery();
  const projectName = projects?.find((p) => p.id === id)?.name;

  return (
    <CollapsibleSectionsProvider>
      <section className="flex flex-col gap-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-text-subtle mb-1 font-body">Proyecto</p>
            <h2 className="text-2xl font-bold text-text-default m-0 font-display">Integraciones</h2>
          </div>
          {data && <SectionCollapseControls />}
        </header>

        {isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
        )}

        {isError && (
          <div className="px-5 py-4 bg-bg-surface border border-border-subtle rounded-xl text-sm text-error">
            Error al cargar las integraciones. Intenta de nuevo.
          </div>
        )}

        {data && (
          <div className="flex flex-col gap-3">
            {/* stockSupported falls back to true when the backend predates `stockIntegration`, keeping the previous behavior. */}
            <UpdateStockSection
              projectId={id}
              projectName={projectName}
              stockSupported={data.stockIntegration?.supported ?? true}
              integrationType={data.stockIntegration?.type}
            />
            <WhatsAppCallingSection projectId={id} />
            <ExternalLinkConfigSection projectId={id} />
            <CalendarSchedulingSection projectId={id} projectName={projectName} />
            <MondaySection data={data.monday} projectId={id} />
            <MondaySupportSection projectId={id} projectName={projectName} />
            <LookerSection data={data.looker} projectId={id} />
            <EmailProcessingSection key={id} data={data.emailProcessing} projectId={id} />
            <CrmConnectionsSection data={data.crmConnections} />
          </div>
        )}
      </section>
    </CollapsibleSectionsProvider>
  );
};

export default IntegrationsView;
